import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Manages the reorderable list of QR label lines on the asset layout admin and
// renders a live preview of the resulting label.
//
// Each row submits type/key/field_id/value via `asset_layout[qr_lines][]`, and
// the visible "source" select drives which hidden inputs are populated.
export default class extends Controller {
  static targets = [
    "container",
    "template",
    "card",
    "previewTitle",
    "previewDivider",
    "previewBody",
  ]

  connect() {
    this.sortable = new Sortable(this.containerTarget, {
      handle: ".handle",
      animation: 150,
      onEnd: () => {
        this.refreshPreview()
        this.save()
      },
    })
    this.refreshPreview()
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  addRow(event) {
    event.preventDefault()
    const clone = this.templateTarget.content.cloneNode(true)
    this.containerTarget.appendChild(clone)
    this.refreshPreview()
    this.save()
  }

  removeRow(event) {
    event.preventDefault()
    const row = event.target.closest(".qr-line")
    if (row) {
      row.remove()
    }
    this.refreshPreview()
    this.save()
  }

  sourceChanged(event) {
    const row = event.target.closest(".qr-line")
    if (!row) {
      return
    }

    const [type, reference] = this.parseValue(event.target.value)
    row.querySelector('[data-qr-role="type"]').value = type
    row.querySelector('[data-qr-role="key"]').value =
      type === "standard" || type === "global" ? reference : ""
    row.querySelector('[data-qr-role="field_id"]').value = type === "field" ? reference : ""

    const textInput = row.querySelector('[data-qr-role="value"]')
    if (type === "text") {
      textInput.classList.remove("hidden")
      textInput.focus()
    } else {
      textInput.classList.add("hidden")
    }

    this.refreshPreview()
  }

  refreshPreview() {
    if (!this.hasCardTarget) {
      return
    }

    const lines = this.currentLines()

    if (lines.length === 0) {
      this.previewTitleTarget.textContent = ""
      this.previewTitleTarget.classList.add("hidden")
    } else {
      this.previewTitleTarget.textContent = lines[0]
      this.previewTitleTarget.classList.remove("hidden")
    }

    const body = lines.slice(1)
    this.previewBodyTarget.innerHTML = ""
    body.forEach((text) => {
      const p = document.createElement("p")
      p.textContent = text
      this.previewBodyTarget.appendChild(p)
    })

    this.previewDividerTarget.classList.toggle("hidden", body.length === 0)
  }

  currentLines() {
    const rows = Array.from(this.containerTarget.querySelectorAll(".qr-line"))
    const lines = []

    rows.forEach((row) => {
      const select = row.querySelector(".qr-line__source")
      if (!select) {
        return
      }

      let text
      if (select.value === "text") {
        text = row.querySelector('[data-qr-role="value"]').value.trim()
      } else {
        const option = select.options[select.selectedIndex]
        text = option ? (option.dataset.preview || "").trim() : ""
      }

      if (text) {
        lines.push(text)
      }
    })

    return lines
  }

  // Add/remove/reorder don't mutate a named input, so they never fire a native
  // input/change event. Dispatch a bubbling change from inside the form so the
  // `form-autosave` controller picks it up and persists the new configuration.
  save() {
    this.containerTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  parseValue(value) {
    const separatorIndex = value.indexOf(":")
    if (separatorIndex === -1) {
      return [value, ""]
    }
    return [value.slice(0, separatorIndex), value.slice(separatorIndex + 1)]
  }
}
